import { useMemo } from 'react'

import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

interface ProfileCompletionData {
	isComplete: boolean
	missingFields: string[]
}

/**
 * Hook to check if user profile is complete by validating account demographic data
 * Now uses Redux state instead of making an API call
 */
export const useProfileCompletion = () => {
	// Get current account (selected or user's account)
	const account = useCurrentAccount()

	// Use the centralized refetch hook
	const refetch = useRefetchAccountData()

	const data = useMemo<ProfileCompletionData>(() => {
		if (!account) {
			return {
				isComplete: false,
				missingFields: [
					'demographic_ages',
					'demographic_socioeconomics',
					'demographic_gender',
				],
			}
		}

		const missingFields: string[] = []

		if (!account.businessCategoryId) {
			missingFields.push('business_category')
		}
		if (!account.demographicAges || account.demographicAges.length === 0) {
			missingFields.push('demographic_ages')
		}

		if (
			!account.demographicSocioeconomics ||
			account.demographicSocioeconomics.length === 0
		) {
			missingFields.push('demographic_socioeconomics')
		}

		if (!account.demographicGender || account.demographicGender === null) {
			missingFields.push('demographic_gender')
		}

		return {
			isComplete: missingFields.length === 0,
			missingFields,
		}
	}, [account])

	// Return an object similar to useQuery to maintain compatibility
	return {
		data,
		isLoading: false, // Data is always available from Redux
		isError: false,
		error: null,
		refetch,
	}
}
